import { useEffect, useState } from "react";

const API = process.env.REACT_APP_API_URL || "https://api.manavastralu.com";
const LS_KEY = "mv_catalog_v1";      // bump the suffix if the data shape ever changes
const FRESH_MS = 10 * 1000;          // younger than this â†’ no need to ask the server again

/**
 * ONE shared copy of the storefront (categories + products) for the whole site.
 *
 *  â€¢ memory     â†’ pages that open later read it with no waiting at all
 *  â€¢ localStorage â†’ the NEXT visit / refresh paints instantly from the last copy
 *  â€¢ /api/storefront is called once, early (see prefetchCatalog), and refreshed
 *    quietly in the background â€” the screen never waits for it once there is a copy.
 */
let memory   = null;                 // { categories, products, at }
let inflight = null;
const listeners = new Set();

const readLS = () => {
  try {
    const v = JSON.parse(localStorage.getItem(LS_KEY));
    return v && Array.isArray(v.categories) && Array.isArray(v.products) ? v : null;
  } catch { return null; }
};

export function getCachedCatalog() {
  if (!memory) memory = readLS();
  return memory;
}

const isFresh = (c) => !!c && Date.now() - c.at < FRESH_MS;

// Normal path: ONE request. If the server doesn't have /api/storefront yet
// (backend not redeployed), build the same data from the two older endpoints
// so the site keeps working instead of showing "Categories are unavailable".
async function fetchStorefront() {
  const r = await fetch(`${API}/api/storefront`);
  if (r.ok) return r.json();
  if (r.status !== 404) throw new Error(`storefront ${r.status}`);

  const [cr, pr] = await Promise.all([
    fetch(`${API}/api/categories`),
    fetch(`${API}/api/products?view=card`),
  ]);
  if (!cr.ok || !pr.ok) throw new Error(`fallback ${cr.status}/${pr.status}`);
  const categories = await cr.json();
  const products   = await pr.json();

  // an even older backend returns [{category, count}] with no ids â€” unusable here
  if (!Array.isArray(categories) || (categories.length && !categories[0].slug)) {
    throw new Error("backend needs to be updated");
  }
  return {
    categories,
    products: (Array.isArray(products) ? products : []).map((p) => {
      if (p.availability) return p;
      const stock = Number(p.stock) || 0;
      const availability = p.soldOut || stock <= 0 ? "sold_out" : stock <= 3 ? "low_stock" : "in_stock";
      return { ...p, availability };
    }),
  };
}

export function refreshCatalog() {
  if (inflight) return inflight;
  inflight = fetchStorefront()
    .then((data) => {
      memory = {
        categories: Array.isArray(data.categories) ? data.categories : [],
        products:   Array.isArray(data.products)   ? data.products   : [],
        at: Date.now(),
      };
      try { localStorage.setItem(LS_KEY, JSON.stringify(memory)); } catch {}
      listeners.forEach((fn) => fn(memory));
      return memory;
    })
    .finally(() => { inflight = null; });
  return inflight;
}

// call once at app start â€” by the time anyone opens a category it is already here
export function prefetchCatalog() {
  if (!isFresh(getCachedCatalog())) refreshCatalog().catch(() => {});
}

export function subscribeCatalog(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * const { data, fresh, error } = useCatalog();
 *   data   â†’ { categories, products } or null (nothing cached yet, still loading)
 *   fresh  â†’ this copy has been confirmed by the server in the last few seconds
 *   error  â†’ nothing cached AND the server could not be reached
 */
export function useCatalog() {
  const [data,  setData]  = useState(getCachedCatalog);
  const [fresh, setFresh] = useState(() => isFresh(getCachedCatalog()));
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    const unsub = subscribeCatalog((d) => {
      if (!alive) return;
      setData(d); setFresh(true); setError(false);
    });

    if (!isFresh(getCachedCatalog())) {
      refreshCatalog().catch(() => {
        if (!alive) return;
        setFresh(true);                                  // we did try
        if (!getCachedCatalog()) setError(true);
      });
    }
    return () => { alive = false; unsub(); };
  }, []);

  return { data, fresh: fresh || isFresh(data), error };
}